import { useState } from "react";
import { useGame, BUILDING_COSTS, BuildingType, POWER_PLANT_SPECS, INDUSTRY_SPECS, PowerPlantType, CompanyIndustry } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import { getLandValueByLatLng, getLandValueDescription } from "@/mocks/landValues";

interface Props { blockId: string; onClose: () => void; }

export default function BuildModal({ blockId, onClose }: Props) {
  const { buildOnBlock, blocks } = useGame();
  const { user, addCoins } = useAuth();
  const block = blocks[blockId];
  const lv = block ? getLandValueByLatLng(block.lat, block.lng) : null;
  const [step, setStep] = useState<"type" | "detail">("type");
  const [selectedType, setSelectedType] = useState<BuildingType | null>(null);
  const [plantType, setPlantType] = useState<PowerPlantType>("solar");
  const [industry, setIndustry] = useState<CompanyIndustry>("technology");
  const [companyName, setCompanyName] = useState("");
  const [plantName, setPlantName] = useState("");
  const [msg, setMsg] = useState("");

  const houseIncome = lv ? lv.houseIncome : 10;
  const companyIncome = lv ? lv.companyIncome : 200;
  const powerPlantIncome = lv ? lv.powerPlantIncome : 1000;
  const houseCost = lv ? lv.houseCost : BUILDING_COSTS.house;
  const companyCost = lv ? lv.companyCost : BUILDING_COSTS.company;
  const powerPlantCost = lv ? lv.powerPlantCost : BUILDING_COSTS.powerPlant;

  const BUILDINGS: { type: BuildingType; icon: string; color: string; bg: string; label: string; desc: string; income: number; cost: number }[] = [
    { type: "house", icon: "ri-home-4-line", color: "text-emerald-400", bg: "bg-emerald-400/10 border-emerald-400/30", label: "住宅", desc: "居住者を受け入れ安定収入", income: houseIncome, cost: houseCost },
    { type: "company", icon: "ri-building-4-line", color: "text-cyan-400", bg: "bg-cyan-400/10 border-cyan-400/30", label: "会社", desc: "従業員採用・製品製造・財務管理", income: companyIncome, cost: companyCost },
    { type: "powerPlant", icon: "ri-flashlight-line", color: "text-violet-400", bg: "bg-violet-400/10 border-violet-400/30", label: "発電所", desc: "電力生産・供給契約で大収益", income: powerPlantIncome, cost: powerPlantCost },
  ];

  const getBuildingCost = (type: BuildingType) => {
    if (type === "house") return houseCost;
    if (type === "company") return companyCost;
    return powerPlantCost;
  };

  const handleSelectType = (type: BuildingType) => {
    if (type === "house") {
      handleBuild(type);
    } else {
      setSelectedType(type);
      setStep("detail");
    }
  };

  const handleBuild = (type: BuildingType, extra?: Record<string, unknown>) => {
    if (!user) return;
    const cost = getBuildingCost(type);
    if (user.coins < cost) { setMsg(`コインが不足しています（必要: ${cost.toLocaleString()} coins）`); return; }
    const result = buildOnBlock(blockId, type, user.id, extra as any);
    if (result.success) {
      addCoins(-cost);
      setMsg("建設完了！");
      setTimeout(onClose, 1200);
    } else {
      setMsg("建設に失敗しました");
    }
  };

  const handleDetailBuild = () => {
    if (selectedType === "company") {
      if (!companyName.trim()) { setMsg("会社名を入力してください"); return; }
      handleBuild("company", { name: companyName, industry });
    } else if (selectedType === "powerPlant") {
      const name = plantName.trim() || POWER_PLANT_SPECS[plantType].label + "発電所";
      handleBuild("powerPlant", { name, type: plantType });
    }
  };

  const isDark = true;
  const inputClass = "bg-white/5 border-white/10 text-white placeholder-gray-600 focus:border-amber-400/50 w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors";

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>
      <div className="relative bg-[#0f1015] border border-white/10 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <div className="flex items-center gap-2">
            <i className="ri-hammer-line text-amber-400 text-base"></i>
            <span className="text-white font-semibold text-sm whitespace-nowrap">
              {step === "type" ? "建物を建てる" : selectedType === "company" ? "会社を設立する" : "発電所を建設する"}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 bg-amber-400/10 rounded-full px-3 py-1">
              <i className="ri-coin-line text-amber-400 text-xs"></i>
              <span className="text-amber-400 text-xs font-semibold whitespace-nowrap">{user?.coins.toLocaleString()} coins</span>
            </div>
            <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-gray-400 cursor-pointer">
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-3">
          {/* 地価情報バナー */}
          {lv && step === "type" && (
            <div className="bg-amber-400/5 border border-amber-400/20 rounded-xl px-3 py-2 space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-base">{lv.flag}</span>
                  <p className="text-amber-300 text-xs font-semibold">{lv.country}</p>
                </div>
                <div className="text-right">
                  <p className="text-gray-400 text-xs">地価係数</p>
                  <p className="text-amber-400 font-bold text-sm">×{lv.landMultiplier}</p>
                </div>
              </div>
              {(lv.avgLandPriceJpyM2 || lv.avgLandPriceUsdM2) && (
                <div className="border-t border-white/5 pt-1.5 flex items-center gap-1.5">
                  <span className="text-gray-600 text-xs">現実参考値:</span>
                  <span className="text-gray-500 text-xs">
                    {lv.avgLandPriceJpyM2
                      ? `公示地価 約${(lv.avgLandPriceJpyM2 / 10000).toFixed(0)}万円/m²`
                      : `約$${lv.avgLandPriceUsdM2?.toLocaleString()}/m²`}
                  </span>
                </div>
              )}
            </div>
          )}

          {step === "type" && BUILDINGS.map((b) => {
            const cost = b.cost;
            const canAfford = (user?.coins || 0) >= cost;
            return (
              <button key={b.type} onClick={() => handleSelectType(b.type)} disabled={!canAfford}
                className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all cursor-pointer text-left ${b.bg} ${canAfford ? "hover:scale-[1.02]" : "opacity-40 cursor-not-allowed"}`}>
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${b.bg}`}>
                  <i className={`${b.icon} ${b.color} text-2xl`}></i>
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm font-semibold whitespace-nowrap">{b.label}</p>
                  <p className="text-gray-400 text-xs">{b.desc}</p>
                  <p className="text-gray-500 text-xs mt-0.5">収入: +{b.income.toLocaleString()} coins/h</p>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-bold whitespace-nowrap ${canAfford ? "text-amber-400" : "text-red-400"}`}>{cost.toLocaleString()}</p>
                  <p className="text-gray-600 text-xs whitespace-nowrap">coins</p>
                </div>
              </button>
            );
          })}

          {step === "detail" && selectedType === "company" && (
            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-400 block mb-1.5">会社名 *</label>
                <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="例: Tokyo Tech Solutions" className={inputClass} />
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1.5">業種</label>
                <select value={industry} onChange={(e) => setIndustry(e.target.value as CompanyIndustry)}
                  className={`${inputClass} cursor-pointer`}>
                  {Object.entries(INDUSTRY_SPECS).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div className={`rounded-xl p-3 bg-cyan-400/5 border border-cyan-400/15`}>
                <p className="text-xs text-gray-400 mb-1">設立コスト</p>
                <p className="text-amber-400 font-bold">{companyCost.toLocaleString()} coins</p>
                <p className="text-xs text-gray-500 mt-1">従業員採用・製品製造・財務管理が可能になります</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setStep("type")} className="flex-1 py-2.5 rounded-xl bg-white/5 text-gray-400 text-sm cursor-pointer hover:bg-white/10 whitespace-nowrap">戻る</button>
                <button onClick={handleDetailBuild} className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 text-black font-bold text-sm cursor-pointer whitespace-nowrap">設立する</button>
              </div>
            </div>
          )}

          {step === "detail" && selectedType === "powerPlant" && (
            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-400 block mb-1.5">発電所名</label>
                <input type="text" value={plantName} onChange={(e) => setPlantName(e.target.value)}
                  placeholder="例: Tokyo Solar Farm" className={inputClass} />
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1.5">発電方式</label>
                <select value={plantType} onChange={(e) => setPlantType(e.target.value as PowerPlantType)}
                  className={`${inputClass} cursor-pointer`}>
                  {Object.entries(POWER_PLANT_SPECS).map(([k, v]) => (
                    <option key={k} value={k}>{v.label} — {v.capacity}MW / 効率{v.efficiency}%</option>
                  ))}
                </select>
              </div>
              {plantType && (
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {[
                    { label: "設備容量", value: `${POWER_PLANT_SPECS[plantType].capacity} MW` },
                    { label: "効率", value: `${POWER_PLANT_SPECS[plantType].efficiency}%` },
                    { label: "CO₂排出", value: `${POWER_PLANT_SPECS[plantType].co2} t/MWh` },
                    { label: "月次メンテ費", value: `${POWER_PLANT_SPECS[plantType].maintenanceCost.toLocaleString()} coins` },
                  ].map((s) => (
                    <div key={s.label} className="rounded-lg p-2.5 bg-white/3 border border-white/8">
                      <p className="text-gray-500 mb-0.5">{s.label}</p>
                      <p className="text-white font-semibold">{s.value}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className="rounded-xl p-3 bg-violet-400/5 border border-violet-400/15">
                <p className="text-xs text-gray-400 mb-1">建設コスト</p>
                <p className="text-amber-400 font-bold">{powerPlantCost.toLocaleString()} coins</p>
                <p className="text-xs text-gray-500 mt-1">電力供給契約で安定した大収益を得られます</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setStep("type")} className="flex-1 py-2.5 rounded-xl bg-white/5 text-gray-400 text-sm cursor-pointer hover:bg-white/10 whitespace-nowrap">戻る</button>
                <button onClick={handleDetailBuild} className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 text-black font-bold text-sm cursor-pointer whitespace-nowrap">建設する</button>
              </div>
            </div>
          )}

          {msg && (
            <div className={`rounded-xl px-3 py-2 text-xs text-center ${msg.includes("完了") ? "bg-emerald-400/10 text-emerald-400" : "bg-red-400/10 text-red-400"}`}>{msg}</div>
          )}
        </div>
      </div>
    </div>
  );
}
